

import React, { useState } from 'react';
import { FaPhone, FaGavel } from 'react-icons/fa';
import { MdOutlineEmail } from "react-icons/md";

// --- Mock Data (Retained) ---
const mockTaskData = {
    summaryMetrics: [
        { title: "TASKS ASSIGNED TODAY", value: 47, color: "text-indigo-400" },
        { title: "TASKS COMPLETED", value: 38, color: "text-green-400" },
        { title: "COMPLETION RATE", value: "81%", color: "text-blue-400" },
        { title: "PENDING TASKS", value: 9, color: "text-red-400" },
    ],
    taskOverview: [
        { id: 1, priority: 'H', customer: 'BuildRight LLC', assignedTo: 'Sarah Chen', description: 'Call regarding 92-day overdue invoice ($67.5K)', dueDate: 'Today 2 PM', status: 'Pending', action: 'Call' },
        { id: 2, priority: 'H', customer: 'FastTrack Delivery', assignedTo: 'Mike Johnson', description: 'Escalate to legal - 105 days overdue', dueDate: 'Today 4 PM', status: 'Overdue', action: 'Legal' },
        { id: 3, priority: 'M', customer: 'Metro Services', assignedTo: 'Sarah Chen', description: 'Follow-up call on payment plan', dueDate: 'Dec 4 10 AM', status: 'Pending', action: 'Call' },
        { id: 4, priority: 'M', customer: 'TechStart Inc', assignedTo: 'Lisa Martinez', description: 'Send friendly reminder email', dueDate: 'Today 5 PM', status: 'Completed', action: 'Email' },
        { id: 5, priority: 'L', customer: 'Retail Partners Co', assignedTo: 'Lisa Martinez', description: 'Send payment confirmation', dueDate: 'Dec 5 9 AM', status: 'Pending', action: 'Email' },
        { id: 6, priority: 'H', customer: 'Digital Dynamics', assignedTo: 'Mike Johnson', description: 'Call to discuss 55-day aging', dueDate: 'Today 3 PM', status: 'Completed', action: 'Call' },
        { id: 7, priority: 'M', customer: 'Acme Corp', assignedTo: 'Sarah Chen', description: 'Thank you email for on-time payment', dueDate: 'Dec 4 11 AM', status: 'Completed', action: 'Email' },
        { id: 8, priority: 'L', customer: 'Global Logistics', assignedTo: 'Lisa Martinez', description: 'File completed AR report for Q3', dueDate: 'Dec 18 1 PM', status: 'Completed', action: 'Email' },
        { id: 9, priority: 'H', customer: 'Innovate Solutions', assignedTo: 'Mike Johnson', description: 'Review trust score deviation', dueDate: 'Dec 17 9 AM', status: 'Pending', action: 'Call' },
    ],
    collectorMetrics: [
        { name: 'Sarah Chen', role: 'Collections Specialist', assigned: 18, completed: 16, collected: '$142K', successRate: '89%' },
        { name: 'Mike Johnson', role: 'Senior Collector', assigned: 15, completed: 11, collected: '$98K', successRate: '73%' },
        { name: 'Lisa Martinez', role: 'AR Coordinator', assigned: 14, completed: 11, collected: '$87K', successRate: '79%' },
    ],
};

// --- Helper Functions for DARKEST & SUBTLE THEME (Frontend Color Logic) ---

const getPriorityStyles = (priority) => {
    // Ultra-subtle background: Very low opacity color wash (15%) on dark gray, with a thin border.
    switch (priority) {
        case 'H': return "bg-red-900/15 text-red-300 border border-red-900/30";
        case 'M': return "bg-yellow-900/15 text-yellow-300 border border-yellow-900/30";
        case 'L': default: return "bg-green-900/15 text-green-300 border border-green-900/30";
    }
};

const getStatusStyles = (status) => {
    // Ultra-subtle background: Very low opacity color wash (15%) on dark gray, with a thin border.
    switch (status) {
        case 'Overdue': return "bg-red-900/15 text-red-300 border border-red-900/30";
        case 'Pending': return "bg-yellow-900/15 text-yellow-300 border border-yellow-900/30";
        case 'Completed': default: return "bg-green-900/15 text-green-300 border border-green-900/30";
    }
};

const getActionIcon = (action) => {
    const baseClasses = "w-4 h-4";
    // Muting icons to 400 shade for less glare
    switch (action) {
        case 'Call': return <FaPhone className={`text-blue-400 ${baseClasses}`} />;
        case 'Legal': return <FaGavel className={`text-red-400 ${baseClasses}`} />;
        case 'Email': return <MdOutlineEmail className={`text-indigo-400 ${baseClasses}`} />;
        default: return null;
    }
};

/**
 * Task Management Dashboard Component (Hyper-Refined Aesthetic).
 */
const TaskManagementDashboard = () => {
    const { summaryMetrics, taskOverview, collectorMetrics } = mockTaskData;

    return (
        // Max dark background (zinc-900), reduced padding
        <div className="p-3 mt-8 text-zinc-100 bg-zinc-900 max-h-[calc(100vh-100px)] overflow-y-auto">

            <h1 className="pb-2 mb-4 text-xl font-extrabold border-b text-zinc-100 border-zinc-700">
                AI Task Management
            </h1>

            {/* 1. Summary Metric Cards - Darker, Denser */}
            <div className="grid grid-cols-2 gap-3 mb-5 md:grid-cols-4">
                {summaryMetrics.map((metric, index) => (
                    <div
                        key={index}
                        // Darker card background (zinc-800), minimal padding (p-3)
                        className="p-3 border rounded-lg shadow-inner bg-zinc-800 border-zinc-700"
                    >
                        <h3 className="mb-1 text-[10px] font-medium tracking-widest uppercase text-zinc-400">
                            {metric.title}
                        </h3>
                        {/* Smaller value font size for density */}
                        <div className={`text-2xl font-extrabold ${metric.color}`}>
                            {metric.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* 2. Detailed Task Table - Primary Component */}
            <div className="flex flex-col p-0 mb-6 overflow-hidden border rounded-lg shadow-lg bg-zinc-800 border-zinc-700">
                
                {/* Table Title */}
                <h2 className="flex-shrink-0 px-4 pt-3 pb-2 text-base font-bold border-b text-zinc-100 border-zinc-700">
                    Task Overview
                </h2>
                
                {/* SCROLLABLE CONTAINER: max-height and hidden scrollbar applied */}
                <div 
                    className="flex-grow mt-0 overflow-x-auto overflow-y-auto no-scrollbar" 
                    style={{ maxHeight: '300px' }}
                >
                    <table className="w-full text-xs table-auto">
                        
                        {/* Table Header - Sticky, darkest background possible (zinc-800) */}
                        <thead className="sticky top-0 z-20">
                            <tr className="tracking-wide text-left uppercase text-zinc-400 bg-zinc-800/95">
                                <th className="px-4 py-2 font-semibold">Priority</th>
                                <th className="px-4 py-2 font-semibold">Customer</th>
                                <th className="px-4 py-2 font-semibold">Assigned To</th>
                                <th className="px-4 py-2 font-semibold">Task Description</th>
                                <th className="px-4 py-2 font-semibold">Due Date</th>
                                <th className="px-4 py-2 font-semibold">Status</th>
                                <th className="px-4 py-2 font-semibold text-center">Action</th>
                            </tr>
                        </thead>
                        
                        <tbody>
                            {/* Table rows use minimal vertical padding (py-2) */}
                            {mockTaskData.taskOverview.map((task) => (
                                <tr 
                                    key={task.id} 
                                    className="transition duration-150 border-t border-zinc-700/50 hover:bg-zinc-700/40"
                                >
                                    {/* Priority Circle */}
                                    <td className="px-4 py-2">
                                        <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${getPriorityStyles(task.priority)}`}>
                                            {task.priority}
                                        </span>
                                    </td>
                                    {/* Customer - Softer link color, reduced max-width */}
                                    <td className="py-2 px-4 font-medium text-blue-300 truncate max-w-[80px] cursor-pointer">{task.customer}</td>
                                    {/* Assigned To */}
                                    <td className="px-4 py-2 text-zinc-300">{task.assignedTo}</td>
                                    {/* Description - Reduced max-width */}
                                    <td className="py-2 px-4 text-zinc-200 max-w-[200px]">{task.description}</td>
                                    {/* Due Date */}
                                    <td className="px-4 py-2 text-zinc-300 whitespace-nowrap">{task.dueDate}</td>
                                    {/* Status Chip - Uses new ultra-subtle color styles */}
                                    <td className="px-4 py-2">
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full ${getStatusStyles(task.status)}`}>
                                            {task.status}
                                        </span>
                                    </td>
                                    {/* Action Icon - Centered, subdued color */}
                                    <td className="flex justify-center px-4 py-2">
                                        {getActionIcon(task.action)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 3. Collector Performance Metrics - Darker, Denser */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {collectorMetrics.map((collector, index) => (
                    <div
                        key={index}
                        // Darker card background (zinc-800), minimal padding (p-4)
                        className="p-4 border rounded-lg shadow-sm bg-zinc-800 border-zinc-700"
                    >
                        <h3 className="text-lg font-bold text-zinc-100">{collector.name}</h3>
                        <p className="mb-3 text-xs text-zinc-400">{collector.role}</p>

                        {/* Metric Grid - Tighter grid, subtle separators, smaller fonts */}
                        <div className="grid grid-cols-2 py-3 text-sm gap-y-3 gap-x-2 border-y border-zinc-700">
                            <div className="pr-2 border-r border-zinc-700">
                                <p className="text-xs text-zinc-400">Assigned</p>
                                <p className="text-lg font-bold text-indigo-400">{collector.assigned}</p>
                            </div>
                            <div>
                                <p className="text-xs text-zinc-400">Completed</p>
                                <p className="text-lg font-bold text-green-400">{collector.completed}</p>
                            </div>
                            <div className="pr-2 border-r border-zinc-700">
                                <p className="text-xs text-zinc-400">Collected</p>
                                <p className="text-sm font-bold text-zinc-100">{collector.collected}</p>
                            </div>
                            <div>
                                <p className="text-xs text-zinc-400">Success Rate</p>
                                <p className="text-sm font-bold text-zinc-100">{collector.successRate}</p>
                            </div>
                        </div>

                        {/* Completion Bar */}
                        <div className="mt-3">
                            <p className="flex justify-between text-[10px] font-medium text-zinc-400">
                                <span>Completion Progress</span>
                            </p>
                            <div className="h-1.5 rounded-full bg-zinc-700 mt-1">
                                <div
                                    className="h-1.5 rounded-full bg-green-500"
                                    style={{ width: collector.successRate }}
                                ></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TaskManagementDashboard;